import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "./PickerModal.scss";

function PickerModal({
  options,
  value,
  onChange,
  placeholder,
  title,
  searchPlaceholder,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewportHeight, setViewportHeight] = useState(null);
  const searchInputRef = useRef(null);
  const modalRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle keyboard visibility using visualViewport API
  useEffect(() => {
    if (!isOpen) return;

    function handleViewportResize() {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      }
    }

    // Set initial height
    handleViewportResize();

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleViewportResize);
      }
      setViewportHeight(null);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  function handleOpen() {
    setIsOpen(true);
    setSearchQuery("");
  }

  function handleClose() {
    setIsOpen(false);
    setSearchQuery("");
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }

  function handleOptionClick(optionValue) {
    onChange(optionValue);
    handleClose();
  }

  return (
    <>
      <button
        type="button"
        className="picker-trigger"
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="picker-trigger-text">{displayText}</span>
        <span className="picker-trigger-arrow">&#9660;</span>
      </button>

      {isOpen && (
        <div
          className="picker-overlay"
          onClick={handleOverlayClick}
          role="presentation"
          style={viewportHeight ? { height: viewportHeight } : undefined}
        >
          <div
            className={`picker-modal ${viewportHeight ? "keyboard-open" : ""}`}
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            <div className="picker-header">
              <h2 className="picker-title">{title}</h2>
              <button
                type="button"
                className="picker-close"
                onClick={handleClose}
                aria-label="סגור"
              >
                &times;
              </button>
            </div>

            <div className="picker-search">
              <input
                ref={searchInputRef}
                type="text"
                className="picker-search-input"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label={searchPlaceholder}
              />
            </div>

            <div className="picker-options" role="listbox">
              {filteredOptions.length === 0 ? (
                <div className="picker-empty">לא נמצאו תוצאות</div>
              ) : (
                filteredOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`picker-option ${
                      opt.value === value ? "selected" : ""
                    }`}
                    onClick={() => handleOptionClick(opt.value)}
                    role="option"
                    aria-selected={opt.value === value}
                  >
                    {opt.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

PickerModal.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  title: PropTypes.string,
  searchPlaceholder: PropTypes.string,
};

PickerModal.defaultProps = {
  placeholder: "בחר...",
  title: "בחירה",
  searchPlaceholder: "חיפוש...",
};

export default PickerModal;
